// @ts-nocheck -- legacy JS-style component ported verbatim from a Claude Artifact.
// Types/strictness deliberately disabled; revisit in a dedicated refactor.
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Plus, Trash2, ChevronDown, ChevronRight, Download, Upload, Settings, FileDown, Save } from "lucide-react";

// ----- ISO week helpers -----
function getISOWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}
function getISOWeekYear(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  return d.getUTCFullYear();
}
function getDateOfISOWeek(week, year) {
  const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
  const dow = simple.getUTCDay();
  const ISOweekStart = simple;
  if (dow <= 4) ISOweekStart.setUTCDate(simple.getUTCDate() - simple.getUTCDay() + 1);
  else ISOweekStart.setUTCDate(simple.getUTCDate() + 8 - simple.getUTCDay());
  return ISOweekStart;
}
function weeksInISOYear(year) {
  // year has 53 weeks if Jan 1 is Thu, or Dec 31 is Thu
  const jan1 = new Date(Date.UTC(year, 0, 1)).getUTCDay();
  const dec31 = new Date(Date.UTC(year, 11, 31)).getUTCDay();
  return jan1 === 4 || dec31 === 4 ? 53 : 52;
}
// Weeks are identified by a key "YYYY-Www" (ISO week-year + zero-padded week),
// so the planning can run across a year boundary. Plain string comparison of
// two keys gives chronological order.
function weekKey(year, week) {
  return `${year}-W${String(week).padStart(2, "0")}`;
}
function parseWeekKey(key) {
  const m = /^(\d{4})-W(\d{2})$/.exec(String(key));
  return m ? { year: Number(m[1]), week: Number(m[2]) } : null;
}
function weekKeyDate(key) {
  const { year, week } = parseWeekKey(key);
  return getDateOfISOWeek(week, year);
}
function addWeeks(key, n) {
  const d = weekKeyDate(key);
  d.setUTCDate(d.getUTCDate() + 7 * n);
  const local = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return weekKey(getISOWeekYear(local), getISOWeek(local));
}
function weekLabel(key) {
  const { year, week } = parseWeekKey(key);
  return week === 1 ? `W1 '${String(year).slice(2)}` : `W${week}`;
}
function formatWeekStart(key) {
  const d = weekKeyDate(key);
  return `${d.getUTCDate()}/${d.getUTCMonth() + 1}`;
}

// Before the planner could span years, hours were keyed by bare week number
// ("43"). All of that data was entered in 2026 (tool launched May 2026).
const LEGACY_HOURS_YEAR = 2026;
function migrateHours(hours) {
  const out = {};
  Object.entries(hours || {}).forEach(([k, v]) => {
    out[/^\d{1,2}$/.test(k) ? weekKey(LEGACY_HOURS_YEAR, Number(k)) : k] = v;
  });
  return out;
}

function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const today = new Date();
const currentWeek = getISOWeek(today);
const currentYear = getISOWeekYear(today);
const currentKey = weekKey(currentYear, currentWeek);
const DEFAULT_RANGE_WEEKS = 26;
const MAX_RANGE_WEEKS = 157; // 3 years; guards against a runaway range

const defaultRows = [
  { id: 1, person: "Sidney Borrego Y Diaz", client: "Topicus", project: "MAP Migration", code: "", hours: {} },
  { id: 2, person: "Jeroen", client: "SD Worx", project: "AWS Resell RFQ", code: "", hours: {} },
];

const defaultRange = { start: currentKey, end: addWeeks(currentKey, DEFAULT_RANGE_WEEKS - 1) };
const endOfNextYearRange = { start: currentKey, end: weekKey(currentYear + 1, weeksInISOYear(currentYear + 1)) };

// Cell shade for the editing table (project line). Keep gentle so it doesn't compete with the summary view.
const editCellShade = (h) => {
  if (!h) return "";
  if (h >= 36) return "bg-red-50";
  if (h >= 28) return "bg-orange-50";
  if (h >= 16) return "bg-amber-50/60";
  if (h > 0) return "bg-emerald-50/60";
  return "";
};

// Person summary shading & text color per spec
function personCellStyle(h) {
  // returns { bg, text }
  if (!h) return { bg: "", text: "text-slate-300" };
  if (h > 40) return { bg: "bg-red-500", text: "text-white font-semibold" };
  if (h >= 40) return { bg: "bg-emerald-500", text: "text-white font-semibold" };
  if (h >= 32) return { bg: "bg-emerald-200", text: "text-emerald-900" };
  if (h < 16) return { bg: "", text: "text-red-700 font-bold" };
  return { bg: "", text: "text-slate-700" };
}

// Client summary: keep simple intensity
function clientCellShade(h) {
  if (!h) return "";
  if (h >= 80) return "bg-blue-200";
  if (h >= 40) return "bg-blue-100";
  if (h > 0) return "bg-blue-50";
  return "";
}

export default function Planner() {
  const [rows, setRows] = useState(defaultRows);
  const [range, setRange] = useState(defaultRange);
  const [expandedPerson, setExpandedPerson] = useState({});
  const [expandedClient, setExpandedClient] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // Rows are only written on an explicit save (button / Cmd+S), not on every
  // keystroke. savedRowsJson is what the server has; dirty = rows differ from it.
  const [savedRowsJson, setSavedRowsJson] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("planning"); // 'planning' | 'person' | 'client' | 'dashboard'
  const [sortBy, setSortBy] = useState({ column: null, dir: null }); // dir: 'asc' | 'desc' | null

  const loadRows = async () => {
    const r = await window.storage.get("rows");
    const loadedRows = r ? JSON.parse(r.value).map((row) => ({ ...row, hours: migrateHours(row.hours) })) : defaultRows;
    setRows(loadedRows);
    setSavedRowsJson(JSON.stringify(loadedRows));
  };

  // Load
  useEffect(() => {
    (async () => {
      try {
        await loadRows();
      } catch {
        setSavedRowsJson(JSON.stringify(defaultRows));
      }
      try {
        const rg = await window.storage.get("range");
        const parsed = rg ? JSON.parse(rg.value) : null;
        // Old single-year saves ({start: 43, end: 52, year}) fall through to the default range.
        if (parsed && parseWeekKey(parsed.start) && parseWeekKey(parsed.end)) {
          // Always snap start forward to current week so old saves don't show past weeks
          const start = parsed.start < currentKey ? currentKey : parsed.start;
          const end = parsed.end < start ? addWeeks(start, DEFAULT_RANGE_WEEKS - 1) : parsed.end;
          setRange({ start, end });
        }
      } catch {}
      setLoaded(true);
    })();
  }, []);

  const rowsJson = useMemo(() => JSON.stringify(rows), [rows]);
  const dirty = loaded && rowsJson !== savedRowsJson;

  const saveRows = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await window.storage.set("rows", rowsJson);
      setSavedRowsJson(rowsJson);
    } catch (err) {
      // 412 + "Server-versie laden" in the conflict dialog → show the server's rows.
      if (err?.status === 412) await loadRows().catch(() => {});
      else alert("Opslaan mislukt: " + (err?.message || err));
    } finally {
      setSaving(false);
    }
  };

  // Cmd/Ctrl+S saves; warn before leaving the page with unsaved changes.
  const saveRef = useRef(saveRows);
  saveRef.current = saveRows;
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        saveRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    if (!loaded) return;
    window.storage.set("range", JSON.stringify(range)).catch(() => {});
  }, [range, loaded]);

  const weeks = useMemo(() => {
    const arr = [];
    for (let k = range.start; k <= range.end && arr.length < MAX_RANGE_WEEKS; k = addWeeks(k, 1)) arr.push(k);
    return arr;
  }, [range]);

  // Suggestion lists from existing data
  const personSuggestions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.person).filter(Boolean))).sort(),
    [rows]
  );
  const clientSuggestions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.client).filter(Boolean))).sort(),
    [rows]
  );
  const projectSuggestions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.project).filter(Boolean))).sort(),
    [rows]
  );
  const codeSuggestions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.code).filter(Boolean))).sort(),
    [rows]
  );

  const addRow = () => {
    const nextId = (rows.reduce((m, r) => Math.max(m, r.id), 0) || 0) + 1;
    setRows([{ id: nextId, person: "", client: "", project: "", code: "", hours: {} }, ...rows]);
  };
  const removeRow = (id) => {
    if (!confirm("Deze rij verwijderen?")) return;
    setRows(rows.filter((r) => r.id !== id));
  };
  const updateField = (id, field, value) => {
    setRows(rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };
  const updateHours = (id, week, value) => {
    const num = value === "" ? "" : Number(value);
    setRows(
      rows.map((r) => {
        if (r.id !== id) return r;
        const h = { ...r.hours };
        if (value === "" || isNaN(num) || num === 0) delete h[week];
        else h[week] = num;
        return { ...r, hours: h };
      })
    );
  };

  // Aggregations
  const totalsByPersonWeek = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      if (!r.person) return;
      if (!m[r.person]) m[r.person] = {};
      weeks.forEach((w) => {
        const v = r.hours[w];
        if (typeof v === "number") m[r.person][w] = (m[r.person][w] || 0) + v;
      });
    });
    return m;
  }, [rows, weeks]);

  const totalsByClientWeek = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      if (!r.client) return;
      if (!m[r.client]) m[r.client] = {};
      weeks.forEach((w) => {
        const v = r.hours[w];
        if (typeof v === "number") m[r.client][w] = (m[r.client][w] || 0) + v;
      });
    });
    return m;
  }, [rows, weeks]);

  const personRowMap = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      if (!r.person) return;
      if (!m[r.person]) m[r.person] = [];
      m[r.person].push(r);
    });
    return m;
  }, [rows]);

  const clientRowMap = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      if (!r.client) return;
      if (!m[r.client]) m[r.client] = [];
      m[r.client].push(r);
    });
    return m;
  }, [rows]);

  const rowTotal = (r) => weeks.reduce((s, w) => s + (r.hours[w] || 0), 0);
  const personWeekTotal = (person, w) => (totalsByPersonWeek[person] || {})[w] || 0;
  const clientWeekTotal = (client, w) => (totalsByClientWeek[client] || {})[w] || 0;
  const personGrandTotal = (p) => weeks.reduce((s, w) => s + personWeekTotal(p, w), 0);
  const clientGrandTotal = (c) => weeks.reduce((s, w) => s + clientWeekTotal(c, w), 0);

  // CSV
  const csvEscape = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[,"\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const exportCsv = () => {
    try {
      const header = ["Persoon", "Klant", "Project", "Tijdschrijfcode", ...weeks, "Totaal"];
      const lines = [header.join(",")];
      rows.forEach((r) => {
        const cells = [
          csvEscape(r.person),
          csvEscape(r.client),
          csvEscape(r.project),
          csvEscape(r.code),
          ...weeks.map((w) => (r.hours[w] != null ? r.hours[w] : "")),
          rowTotal(r),
        ];
        lines.push(cells.join(","));
      });
      const csvContent = lines.join("\n");

      // Method 1: Try Blob + download
      try {
        const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `planning-${new Date().toISOString().slice(0, 10)}.csv`;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 100);
        return;
      } catch (e1) {
        console.warn("Blob download failed, trying data URI", e1);
      }

      // Method 2: Fallback to data URI
      try {
        const dataUri = "data:text/csv;charset=utf-8,\uFEFF" + encodeURIComponent(csvContent);
        const a = document.createElement("a");
        a.href = dataUri;
        a.download = `planning-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        return;
      } catch (e2) {
        console.warn("Data URI download failed, opening in new window", e2);
      }

      // Method 3: Open in new window as last resort
      const win = window.open("");
      if (win) {
        win.document.write("<pre>" + csvContent.replace(/</g, "&lt;") + "</pre>");
        win.document.title = "Planning CSV - kopieer met Ctrl+A, Ctrl+C";
      } else {
        alert("Kan CSV niet downloaden. Inhoud:\n\n" + csvContent.slice(0, 2000));
      }
    } catch (err) {
      console.error("CSV export error:", err);
      alert("Export mislukt: " + err.message);
    }
  };
  const parseCsvLine = (line) => {
    const out = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQ) {
        if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (c === '"') inQ = false;
        else cur += c;
      } else {
        if (c === ",") { out.push(cur); cur = ""; }
        else if (c === '"') inQ = true;
        else cur += c;
      }
    }
    out.push(cur);
    return out;
  };
  const importCsv = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result;
        const lines = text.split(/\r?\n/).filter((l) => l.trim());
        if (lines.length < 2) return;
        const header = parseCsvLine(lines[0]);
        const weekCols = [];
        header.forEach((h, i) => {
          // "2027-W03" (current export) or bare "W43" (older exports → start year of the range)
          const m = /^(?:(\d{4})-)?W(\d{1,2})$/i.exec(h.trim());
          if (m) {
            const y = m[1] ? Number(m[1]) : parseWeekKey(range.start).year;
            weekCols.push({ index: i, week: weekKey(y, Number(m[2])) });
          }
        });
        const newRows = [];
        let nextId = 1;
        for (let i = 1; i < lines.length; i++) {
          const cells = parseCsvLine(lines[i]);
          const hours = {};
          weekCols.forEach(({ index, week }) => {
            const v = cells[index];
            if (v && !isNaN(Number(v)) && Number(v) !== 0) hours[week] = Number(v);
          });
          // Detect if column 4 is the code (header W1 starts after); fallback to empty
          const codeIdx = header.findIndex((h) => /tijdschrijf|code/i.test(h.trim()));
          newRows.push({
            id: nextId++,
            person: cells[0] || "",
            client: cells[1] || "",
            project: cells[2] || "",
            code: codeIdx >= 0 ? cells[codeIdx] || "" : "",
            hours,
          });
        }
        setRows(newRows);
      } catch (err) {
        alert("Import mislukt: " + err.message);
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };
  const reset = () => {
    if (!confirm("Alles wissen en opnieuw beginnen?")) return;
    setRows(defaultRows);
    setRange(defaultRange);
  };

  return (
    <div className="p-4 max-w-full font-sans text-sm text-slate-800">
      <style>{`
        input.no-spin::-webkit-outer-spin-button,
        input.no-spin::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input.no-spin {
          -moz-appearance: textfield;
          appearance: textfield;
        }
      `}</style>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h1 className="text-xl font-semibold mr-auto">Consultant Planning</h1>
        <span className="text-xs text-slate-500 mr-2">
          Huidige week: W{currentWeek} ({formatWeekStart(currentKey)})
        </span>
        <span className={`text-xs mr-1 ${dirty ? "text-amber-700 font-medium" : "text-slate-400"}`}>
          {saving ? "Opslaan…" : dirty ? "Niet-opgeslagen wijzigingen" : loaded ? "Alles opgeslagen" : ""}
        </span>
        <button
          onClick={saveRows}
          disabled={!dirty || saving}
          title="Opslaan (Cmd/Ctrl+S)"
          className="flex items-center gap-1 px-3 py-1.5 rounded font-medium bg-slate-800 text-white hover:bg-slate-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
        >
          <Save size={14} /> Opslaan
        </button>
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="flex items-center gap-1 px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50"
        >
          <Settings size={14} /> Weekbereik
        </button>
        <label className="flex items-center gap-1 px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50 cursor-pointer">
          <Upload size={14} /> Import CSV
          <input type="file" accept=".csv" onChange={importCsv} className="hidden" />
        </label>
        <button
          onClick={exportCsv}
          className="flex items-center gap-1 px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50"
        >
          <Download size={14} /> Export CSV
        </button>
        <button
          onClick={reset}
          className="px-3 py-1.5 border border-slate-300 rounded hover:bg-red-50 text-red-700"
        >
          Reset
        </button>
      </div>

      {showSettings && (
        <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded flex flex-wrap items-center gap-3">
          {[
            ["start", "Van"],
            ["end", "tot en met"],
          ].map(([bound, label]) => {
            const { year, week } = parseWeekKey(range[bound]);
            const setBound = (y, w) => {
              const yy = Math.max(2000, Math.min(2100, y || currentYear));
              const ww = Math.max(1, Math.min(weeksInISOYear(yy), w || 1));
              setRange({ ...range, [bound]: weekKey(yy, ww) });
            };
            return (
              <React.Fragment key={bound}>
                <span>{label} week</span>
                <input
                  type="number"
                  min={1}
                  max={weeksInISOYear(year)}
                  value={week}
                  onChange={(e) => setBound(year, Number(e.target.value))}
                  className="w-16 px-2 py-1 border border-slate-300 rounded"
                />
                <span>jaar</span>
                <input
                  type="number"
                  value={year}
                  onChange={(e) => setBound(Number(e.target.value), week)}
                  className="w-20 px-2 py-1 border border-slate-300 rounded"
                />
              </React.Fragment>
            );
          })}
          <button
            onClick={() => setRange(defaultRange)}
            className="px-2 py-1 text-xs border border-slate-300 rounded hover:bg-white"
          >
            Reset naar huidige week + {DEFAULT_RANGE_WEEKS} weken
          </button>
          <button
            onClick={() => setRange(endOfNextYearRange)}
            className="px-2 py-1 text-xs border border-slate-300 rounded hover:bg-white"
          >
            Huidige week → einde {currentYear + 1}
          </button>
          <span className="text-slate-500">({weeks.length} weken)</span>
        </div>
      )}

      {/* Planning verplaatst naar Tab */}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-3">
        <button
          onClick={() => setActiveTab("planning")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            activeTab === "planning"
              ? "border-slate-800 text-slate-900"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Consulting Planning
        </button>
        <button
          onClick={() => setActiveTab("person")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            activeTab === "person"
              ? "border-slate-800 text-slate-900"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Per medewerker
        </button>
        <button
          onClick={() => setActiveTab("client")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            activeTab === "client"
              ? "border-slate-800 text-slate-900"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Per klant
        </button>
        <button
          onClick={() => setActiveTab("dashboard")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            activeTab === "dashboard"
              ? "border-slate-800 text-slate-900"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Dashboard
        </button>
      </div>

      {activeTab === "planning" && (
        <PlanningTable
          rows={rows}
          weeks={weeks}
          personSuggestions={personSuggestions}
          clientSuggestions={clientSuggestions}
          projectSuggestions={projectSuggestions}
          codeSuggestions={codeSuggestions}
          updateField={updateField}
          updateHours={updateHours}
          addRow={addRow}
          removeRow={removeRow}
          rowTotal={rowTotal}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />
      )}

      {activeTab === "person" && (
        <SummaryBlock
          variant="person"
          groupMap={personRowMap}
          weeks={weeks}
          expanded={expandedPerson}
          setExpanded={setExpandedPerson}
          otherKey="client"
          otherLabel="Klant"
          weekTotal={personWeekTotal}
          grandTotal={personGrandTotal}
          showPdfExport={true}
        />
      )}
      {activeTab === "client" && (
        <SummaryBlock
          variant="client"
          groupMap={clientRowMap}
          weeks={weeks}
          expanded={expandedClient}
          setExpanded={setExpandedClient}
          otherKey="person"
          otherLabel="Persoon"
          weekTotal={clientWeekTotal}
          grandTotal={clientGrandTotal}
        />
      )}

      {activeTab === "dashboard" && (
        <Dashboard
          rows={rows}
          weeks={weeks}
          totalsByPersonWeek={totalsByPersonWeek}
          totalsByClientWeek={totalsByClientWeek}
        />
      )}

      {activeTab === "person" && (
        <div className="mt-3 text-xs text-slate-500 flex flex-wrap gap-3 items-center">
          <span className="font-medium">Legenda:</span>
          <span className="text-red-700 font-bold">&lt; 16u rood getal</span>
          <span><span className="inline-block w-3 h-3 align-middle bg-emerald-200 border border-emerald-300 mr-1"></span>32–39,9u licht groen</span>
          <span><span className="inline-block w-3 h-3 align-middle bg-emerald-500 border border-emerald-600 mr-1"></span>40u groen</span>
          <span><span className="inline-block w-3 h-3 align-middle bg-red-500 border border-red-600 mr-1"></span>&gt; 40u rood</span>
        </div>
      )}

      <div className="mt-6 text-xs text-slate-500">
        Wijzigingen in de planning worden pas gedeeld na Opslaan (Cmd/Ctrl+S). Gebruik Export CSV om een backup te maken.
      </div>
    </div>
  );
}

// ---------------- Autocomplete input ----------------
function AutocompleteInput({ value, onChange, suggestions, placeholder }) {
  const [isOpen, setIsOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(-1);
  const wrapRef = useRef(null);

  // Filter matches. If empty input + focused, show top-N alphabetically.
  const matches = useMemo(() => {
    const q = String(value || "").toLowerCase().trim();
    const all = suggestions.filter((s) => s && typeof s === "string");
    if (!q) {
      return all.slice(0, 8); // show all when empty
    }
    return all
      .filter((s) => s.toLowerCase().includes(q) && s.toLowerCase() !== q)
      .slice(0, 8);
  }, [value, suggestions]);

  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const pick = (s) => {
    onChange(s);
    setIsOpen(false);
    setFocusIdx(-1);
  };

  const show = isOpen && matches.length > 0;

  return (
    <div className="relative" ref={wrapRef}>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => {
          onChange(e.target.value);
          setIsOpen(true);
          setFocusIdx(-1);
        }}
        onFocus={() => setIsOpen(true)}
        onClick={() => setIsOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setIsOpen(false);
            return;
          }
          if (e.key === "ArrowDown") {
            if (matches.length) {
              e.preventDefault();
              setIsOpen(true);
              setFocusIdx((i) => Math.min(matches.length - 1, i + 1));
            }
          } else if (e.key === "ArrowUp") {
            if (matches.length) {
              e.preventDefault();
              setFocusIdx((i) => Math.max(0, i - 1));
            }
          } else if (e.key === "Enter") {
            if (focusIdx >= 0 && matches[focusIdx]) {
              e.preventDefault();
              pick(matches[focusIdx]);
            }
          } else if (e.key === "Tab" && matches.length && isOpen && focusIdx >= 0) {
            e.preventDefault();
            pick(matches[focusIdx]);
          }
        }}
        className="w-full px-2 py-1.5 bg-transparent outline-none focus:bg-blue-50"
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
      />
      {show && (
        <div className="absolute z-50 left-0 right-0 top-full mt-0.5 bg-white border border-slate-300 rounded shadow-lg max-h-48 overflow-y-auto">
          {matches.map((s, i) => (
            <div
              key={s}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(s);
              }}
              onMouseEnter={() => setFocusIdx(i)}
              className={`px-2 py-1 cursor-pointer text-sm ${
                i === focusIdx ? "bg-blue-100" : "hover:bg-slate-100"
              }`}
            >
              {highlightMatch(s, value)}
            </div>
          ))}
        </div>
      )}
      {/* Debug indicator: small dot bottom-right showing # suggestions available */}
      {suggestions.length > 0 && (
        <div className="absolute right-1 top-1/2 -translate-y-1/2 text-[9px] text-slate-300 pointer-events-none select-none">
          {suggestions.length}
        </div>
      )}
    </div>
  );
}

function highlightMatch(text, query) {
  if (!query) return text;
  const q = String(query).toLowerCase().trim();
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q);
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <strong className="text-blue-700">{text.slice(idx, idx + q.length)}</strong>
      {text.slice(idx + q.length)}
    </>
  );
}

// ---------------- Planning table ----------------
function PlanningTable({
  rows,
  weeks,
  personSuggestions,
  clientSuggestions,
  projectSuggestions,
  codeSuggestions,
  updateField,
  updateHours,
  addRow,
  removeRow,
  rowTotal,
  sortBy,
  setSortBy,
}) {
  const toggleSort = (col) => {
    if (sortBy.column !== col) setSortBy({ column: col, dir: "asc" });
    else if (sortBy.dir === "asc") setSortBy({ column: col, dir: "desc" });
    else setSortBy({ column: null, dir: null });
  };

  const sortedRows = useMemo(() => {
    if (!sortBy.column || !sortBy.dir) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = (a[sortBy.column] || "").toString().toLowerCase();
      const bv = (b[sortBy.column] || "").toString().toLowerCase();
      if (av < bv) return sortBy.dir === "asc" ? -1 : 1;
      if (av > bv) return sortBy.dir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, sortBy]);

  return (
    <div className="border border-slate-200 rounded">
      <div className="p-2 border-b border-slate-200 bg-slate-50">
        <button
          onClick={addRow}
          className="flex items-center gap-1 px-3 py-1 text-slate-700 hover:bg-white border border-slate-300 rounded"
        >
          <Plus size={14} /> Rij toevoegen
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="border-collapse">
        <thead className="bg-slate-100 sticky top-0">
          <tr>
            <th className="border-b border-slate-200" rowSpan={2}></th>
            <th className="text-left px-2 py-1.5 border-b border-slate-200 bg-slate-100 min-w-[180px]" rowSpan={2}>
              <SortHeader label="Persoon" col="person" sortBy={sortBy} onToggle={toggleSort} />
            </th>
            <th className="text-left px-2 py-1.5 border-b border-slate-200 min-w-[250px]" rowSpan={2}>
              <SortHeader label="Klant" col="client" sortBy={sortBy} onToggle={toggleSort} />
            </th>
            <th className="text-left px-2 py-1.5 border-b border-slate-200 min-w-[200px]" rowSpan={2}>
              <SortHeader label="Project" col="project" sortBy={sortBy} onToggle={toggleSort} />
            </th>
            <th className="text-left px-2 py-1.5 border-b border-slate-200 min-w-[140px]" rowSpan={2}>Tijdschrijfcode</th>
            {weeks.map((w) => (
              <th key={w} className="text-center px-1 pt-1.5 pb-0 border-slate-200 font-medium text-slate-600 min-w-[58px] leading-tight">
                {weekLabel(w)}
              </th>
            ))}
            <th className="text-center px-2 py-1.5 border-b border-slate-200 bg-slate-200 min-w-[60px]" rowSpan={2}>Tot</th>
          </tr>
          <tr>
            {weeks.map((w) => (
              <th key={w} className="text-center px-1 pb-1.5 border-b border-slate-200 text-[10px] text-slate-400 font-normal leading-tight">
                {formatWeekStart(w)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((r) => (
            <tr key={r.id} className="hover:bg-slate-50 group">
              <td className="border-b border-slate-100 px-1">
                <button
                  onClick={() => removeRow(r.id)}
                  className="text-slate-400 hover:text-red-600 hover:bg-red-50 p-1 rounded"
                  title="Rij verwijderen"
                >
                  <Trash2 size={14} />
                </button>
              </td>
              <td className="border-b border-slate-100">
                <AutocompleteInput
                  value={r.person}
                  onChange={(v) => updateField(r.id, "person", v)}
                  suggestions={personSuggestions}
                  placeholder="Naam"
                />
              </td>
              <td className="border-b border-slate-100">
                <AutocompleteInput
                  value={r.client}
                  onChange={(v) => updateField(r.id, "client", v)}
                  suggestions={clientSuggestions}
                  placeholder="Klant"
                />
              </td>
              <td className="border-b border-slate-100">
                <AutocompleteInput
                  value={r.project}
                  onChange={(v) => updateField(r.id, "project", v)}
                  suggestions={projectSuggestions}
                  placeholder="Project"
                />
              </td>
              <td className="border-b border-slate-100">
                <AutocompleteInput
                  value={r.code}
                  onChange={(v) => updateField(r.id, "code", v)}
                  suggestions={codeSuggestions}
                  placeholder="Code"
                />
              </td>
              {weeks.map((w, wIdx) => {
                const v = r.hours[w];
                return (
                  <td key={w} className={`border-b border-slate-100 ${editCellShade(v)}`}>
                    <input
                      type="number"
                      min={0}
                      step={0.1}
                      value={v ?? ""}
                      data-row={r.id}
                      data-week={w}
                      onChange={(e) => updateHours(r.id, w, e.target.value)}
                      onKeyDown={(e) => {
                        const key = e.key;
                        if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Enter", "Tab"].includes(key)) {
                          // Allow normal arrow left/right inside number unless at edge
                          const input = e.currentTarget;
                          const atStart = input.selectionStart === 0 && input.selectionEnd === 0;
                          const atEnd = input.selectionStart === input.value.length && input.selectionEnd === input.value.length;
                          if (key === "ArrowLeft" && !atStart) return;
                          if (key === "ArrowRight" && !atEnd) return;

                          e.preventDefault();
                          const currentRowIdx = sortedRows.findIndex((row) => row.id === r.id);
                          let nextRowId = r.id;
                          let nextWeek = w;
                          if (key === "ArrowRight" || (key === "Tab" && !e.shiftKey) || key === "Enter") {
                            if (wIdx < weeks.length - 1) {
                              nextWeek = weeks[wIdx + 1];
                            } else if (currentRowIdx < sortedRows.length - 1) {
                              nextRowId = sortedRows[currentRowIdx + 1].id;
                              nextWeek = weeks[0];
                            }
                          } else if (key === "ArrowLeft" || (key === "Tab" && e.shiftKey)) {
                            if (wIdx > 0) {
                              nextWeek = weeks[wIdx - 1];
                            } else if (currentRowIdx > 0) {
                              nextRowId = sortedRows[currentRowIdx - 1].id;
                              nextWeek = weeks[weeks.length - 1];
                            }
                          } else if (key === "ArrowDown") {
                            if (currentRowIdx < sortedRows.length - 1) {
                              nextRowId = sortedRows[currentRowIdx + 1].id;
                            }
                          } else if (key === "ArrowUp") {
                            if (currentRowIdx > 0) {
                              nextRowId = sortedRows[currentRowIdx - 1].id;
                            }
                          }
                          const sel = document.querySelector(
                            `input[data-row="${nextRowId}"][data-week="${nextWeek}"]`
                          );
                          if (sel) {
                            sel.focus();
                            sel.select();
                          }
                        }
                      }}
                      className="no-spin w-full px-1 py-1.5 bg-transparent outline-none text-center focus:bg-blue-50"
                    />
                  </td>
                );
              })}
              <td className="border-b border-slate-100 bg-slate-50 text-center font-medium px-2">
                {rowTotal(r) || ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function SortHeader({ label, col, sortBy, onToggle }) {
  const active = sortBy.column === col;
  const dir = active ? sortBy.dir : null;
  return (
    <button
      onClick={() => onToggle(col)}
      className="flex items-center gap-1 hover:text-blue-600 group"
      title={
        dir === "asc" ? "Klik voor Z→A" : dir === "desc" ? "Klik om sortering te wissen" : "Klik voor A→Z"
      }
    >
      <span>{label}</span>
      <span className={`text-[10px] leading-none ${active ? "text-blue-600" : "text-slate-400 group-hover:text-slate-600"}`}>
        {dir === "asc" ? "▲ A→Z" : dir === "desc" ? "▼ Z→A" : "↕"}
      </span>
    </button>
  );
}

// ---------------- Dashboard ----------------
const NON_PERSON_NAMES = ["zresell", "zonboarding", "zmanaged", "unplanned"];

// "Z_<iets>" = placeholder-medewerker: herinnering om nog een echte consultant
// toe te wijzen. Blijft zichtbaar in de planning, telt niet mee in het dashboard.
const PLACEHOLDER_PERSON = /^z_/i;

function isRealPerson(name) {
  if (!name) return false;
  const n = name.toLowerCase().trim();
  return !NON_PERSON_NAMES.includes(n) && !PLACEHOLDER_PERSON.test(n);
}

function Dashboard({ rows, weeks, totalsByPersonWeek, totalsByClientWeek }) {
  const allPeople = Object.keys(totalsByPersonWeek);
  const people = allPeople.filter(isRealPerson); // alleen echte medewerkers
  const clients = Object.keys(totalsByClientWeek);
  const projects = Array.from(new Set(rows.map((r) => r.project).filter(Boolean)));

  // Geplande uren door echte medewerkers (voor bezettingsgraad)
  const realPlannedHours = rows.reduce((s, r) => {
    if (!isRealPerson(r.person)) return s;
    return s + weeks.reduce((ss, w) => ss + (r.hours[w] || 0), 0);
  }, 0);

  // Totale uren inclusief niet-medewerkers (voor info)
  const totalHours = rows.reduce(
    (s, r) => s + weeks.reduce((ss, w) => ss + (r.hours[w] || 0), 0),
    0
  );

  const capacity = people.length * weeks.length * 40;
  const utilisation = capacity > 0 ? (realPlannedHours / capacity) * 100 : 0;

  // % per week — alleen echte medewerkers tellen mee
  const weeklyPct = weeks.map((w) => {
    const planned = people.reduce((s, p) => s + (totalsByPersonWeek[p][w] || 0), 0);
    const cap = people.length * 40;
    return {
      week: w,
      planned,
      capacity: cap,
      pct: cap ? (planned / cap) * 100 : 0,
      dateLabel: formatWeekStart(w),
    };
  });

  // % per maand
  const monthsMap = {};
  weeks.forEach((w) => {
    const monday = weekKeyDate(w);
    const thursday = new Date(monday);
    thursday.setUTCDate(monday.getUTCDate() + 3);
    const key = `${thursday.getUTCFullYear()}-${String(thursday.getUTCMonth() + 1).padStart(2, "0")}`;
    if (!monthsMap[key]) monthsMap[key] = { key, year: thursday.getUTCFullYear(), month: thursday.getUTCMonth(), weeks: [] };
    monthsMap[key].weeks.push(w);
  });
  const months = Object.values(monthsMap).sort((a, b) => a.key.localeCompare(b.key));
  const monthNames = ["Jan", "Feb", "Mrt", "Apr", "Mei", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dec"];
  const monthly = months.map((m) => {
    const planned = m.weeks.reduce((s, w) => s + people.reduce((ss, p) => ss + (totalsByPersonWeek[p][w] || 0), 0), 0);
    const cap = people.length * m.weeks.length * 40;
    return {
      label: `${monthNames[m.month]} ${m.year}`,
      shortLabel: monthNames[m.month],
      planned,
      capacity: cap,
      weeks: m.weeks.length,
      pct: cap ? (planned / cap) * 100 : 0,
    };
  });

  // Top klanten (alle uren tellen mee, ook van Z*-regels)
  const clientTotals = clients
    .map((c) => ({
      name: c,
      total: weeks.reduce((s, w) => s + (totalsByClientWeek[c][w] || 0), 0),
    }))
    .filter((x) => x.total > 0)
    .sort((a, b) => b.total - a.total);

  // Top projecten
  const projectAgg = {};
  rows.forEach((r) => {
    if (!r.project) return;
    const key = `${r.project}|${r.client || "—"}`;
    if (!projectAgg[key]) projectAgg[key] = { project: r.project, client: r.client || "—", total: 0 };
    weeks.forEach((w) => {
      projectAgg[key].total += r.hours[w] || 0;
    });
  });
  const projectTotals = Object.values(projectAgg)
    .filter((p) => p.total > 0)
    .sort((a, b) => b.total - a.total);

  // Per-medewerker utilization — alleen echte medewerkers
  const personSummary = people
    .map((p) => {
      const total = weeks.reduce((s, w) => s + (totalsByPersonWeek[p][w] || 0), 0);
      const weeklyAvg = weeks.length ? total / weeks.length : 0;
      let under = 0;
      let over = 0;
      let critical = 0;
      weeks.forEach((w) => {
        const v = totalsByPersonWeek[p][w] || 0;
        if (v < 16) under++;
        if (v > 32) over++;
        if (v > 40) critical++;
      });
      return { name: p, total, weeklyAvg, under, over, critical, util: (weeklyAvg / 40) * 100 };
    })
    .sort((a, b) => b.total - a.total);

  const pctColor = (pct) =>
    pct > 100 ? "bg-red-500" :
    pct >= 90 ? "bg-emerald-500" :
    pct >= 60 ? "bg-emerald-300" :
    pct >= 30 ? "bg-amber-300" :
    "bg-slate-200";

  const pctTextColor = (pct) =>
    pct > 100 ? "text-red-600" :
    pct >= 90 ? "text-emerald-600" :
    pct >= 60 ? "text-emerald-700" :
    pct >= 30 ? "text-amber-600" :
    "text-slate-500";

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Totaal geplande uren" value={totalHours.toFixed(0)} sub={`${realPlannedHours.toFixed(0)}u door medewerkers`} />
        <KpiCard
          label="Bezettingsgraad totaal"
          value={`${utilisation.toFixed(0)}%`}
          sub={`${realPlannedHours.toFixed(0)}u / ${capacity.toFixed(0)}u capaciteit`}
          accent={utilisation > 100 ? "red" : utilisation > 80 ? "emerald" : "amber"}
        />
        <KpiCard label="Medewerkers" value={people.length} sub={allPeople.length > people.length ? `+${allPeople.length - people.length} niet-medewerker` : null} />
        <KpiCard label="Klanten" value={clients.length} />
        <KpiCard label="Projecten" value={projects.length} />
      </div>

      {/* % gepland per week */}
      <div className="border border-slate-200 rounded p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">% Gepland per week</h3>
          <span className="text-xs text-slate-500">Norm = 40u × {people.length} medewerker(s) = {people.length * 40}u/week</span>
        </div>

        <div className="overflow-x-auto">
          <div className="flex items-end gap-1 h-44 min-w-fit">
            {weeklyPct.map((wp) => {
              const barHeight = Math.min(120, wp.pct);
              return (
                <div key={wp.week} className="flex flex-col items-center group" style={{ minWidth: "44px" }}>
                  <div className={`text-[10px] tabular-nums mb-1 ${pctTextColor(wp.pct)} font-semibold`}>
                    {wp.pct.toFixed(0)}%
                  </div>
                  <div className="w-full h-28 bg-slate-100 rounded-sm relative flex items-end overflow-hidden">
                    <div
                      className={`${pctColor(wp.pct)} w-full transition-all`}
                      style={{ height: `${(barHeight / 120) * 100}%` }}
                      title={`${weekLabel(wp.week)} (${wp.dateLabel}): ${wp.planned}u / ${wp.capacity}u`}
                    />
                    {/* 100% line */}
                    <div className="absolute left-0 right-0 border-t border-dashed border-slate-400/60" style={{ bottom: `${(100 / 120) * 100}%` }} />
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1 leading-tight">{weekLabel(wp.week)}</div>
                  <div className="text-[9px] text-slate-400 leading-tight">{wp.dateLabel}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-500">
          <span><span className="inline-block w-3 h-3 align-middle bg-slate-200 mr-1"></span>&lt;30%</span>
          <span><span className="inline-block w-3 h-3 align-middle bg-amber-300 mr-1"></span>30–60%</span>
          <span><span className="inline-block w-3 h-3 align-middle bg-emerald-300 mr-1"></span>60–90%</span>
          <span><span className="inline-block w-3 h-3 align-middle bg-emerald-500 mr-1"></span>90–100%</span>
          <span><span className="inline-block w-3 h-3 align-middle bg-red-500 mr-1"></span>&gt;100%</span>
          <span className="ml-auto text-slate-400">— — — 100% lijn</span>
        </div>
      </div>

      {/* % gepland per maand */}
      <div className="border border-slate-200 rounded p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">% Gepland per maand</h3>
          <span className="text-xs text-slate-500">ISO-week valt in maand van donderdag</span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {monthly.map((m) => (
            <div key={m.label} className="border border-slate-200 rounded p-3 bg-white">
              <div className="flex items-baseline justify-between mb-2">
                <div className="font-medium">{m.label}</div>
                <div className={`text-xl font-bold tabular-nums ${pctTextColor(m.pct)}`}>{m.pct.toFixed(0)}%</div>
              </div>
              <div className="h-2 bg-slate-100 rounded overflow-hidden relative">
                <div className={`${pctColor(m.pct)} h-full transition-all`} style={{ width: `${Math.min(100, m.pct)}%` }} />
                {m.pct > 100 && (
                  <div className="absolute top-0 bottom-0 border-r-2 border-red-700" style={{ left: "100%" }} />
                )}
              </div>
              <div className="text-[11px] text-slate-500 mt-1 tabular-nums">
                {m.planned.toFixed(0)}u / {m.capacity}u · {m.weeks}w
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Bezetting per medewerker */}
        <div className="border border-slate-200 rounded p-4">
          <h3 className="font-semibold mb-3">Bezetting per medewerker</h3>
          <div className="space-y-2">
            {personSummary.length === 0 && <div className="text-slate-400 text-sm">Geen data</div>}
            {personSummary.map((p) => {
              const pct = Math.min(150, p.util);
              const barColor =
                p.util > 100 ? "bg-red-500" :
                p.util >= 90 ? "bg-emerald-500" :
                p.util >= 60 ? "bg-emerald-300" :
                p.util >= 30 ? "bg-amber-300" :
                "bg-red-300";
              return (
                <div key={p.name}>
                  <div className="flex items-center justify-between text-xs mb-0.5">
                    <span className="font-medium text-slate-700 truncate mr-2">{p.name}</span>
                    <span className="text-slate-500 tabular-nums">
                      {p.weeklyAvg.toFixed(1)}u/w · <span className={pctTextColor(p.util)}>{p.util.toFixed(0)}%</span>
                    </span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded overflow-hidden relative">
                    <div className={`${barColor} h-full transition-all`} style={{ width: `${(pct / 150) * 100}%` }} />
                    <div className="absolute top-0 bottom-0 border-r border-slate-400/40" style={{ left: `${(100 / 150) * 100}%` }} />
                  </div>
                  {(p.under > 0 || p.over > 0 || p.critical > 0) && (
                    <div className="flex gap-2 mt-1 text-[10px] text-slate-500">
                      {p.under > 0 && <span className="text-red-700 font-semibold">{p.under}w &lt;16u</span>}
                      {p.over > 0 && <span className="text-emerald-700">{p.over}w &gt;32u</span>}
                      {p.critical > 0 && <span className="text-red-700 font-semibold">{p.critical}w &gt;40u</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Top klanten */}
        <div className="border border-slate-200 rounded p-4">
          <h3 className="font-semibold mb-3">Top klanten</h3>
          <div className="space-y-2">
            {clientTotals.length === 0 && <div className="text-slate-400 text-sm">Geen data</div>}
            {clientTotals.slice(0, 10).map((c) => {
              const max = clientTotals[0]?.total || 1;
              const pct = (c.total / max) * 100;
              return (
                <div key={c.name}>
                  <div className="flex items-center justify-between text-xs mb-0.5">
                    <span className="font-medium text-slate-700 truncate mr-2">{c.name}</span>
                    <span className="text-slate-500 tabular-nums">{c.total.toFixed(0)}u</span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded overflow-hidden">
                    <div className="bg-blue-400 h-full" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Top projecten */}
      <div className="border border-slate-200 rounded p-4">
        <h3 className="font-semibold mb-3">Top projecten</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200">
                <th className="py-1.5 font-medium">Project</th>
                <th className="py-1.5 font-medium">Klant</th>
                <th className="py-1.5 font-medium text-right">Uren</th>
                <th className="py-1.5 font-medium w-1/3">Aandeel</th>
              </tr>
            </thead>
            <tbody>
              {projectTotals.length === 0 && (
                <tr><td colSpan={4} className="py-3 text-center text-slate-400">Geen data</td></tr>
              )}
              {projectTotals.slice(0, 12).map((p, i) => {
                const max = projectTotals[0]?.total || 1;
                const pct = (p.total / max) * 100;
                return (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-1.5">{p.project}</td>
                    <td className="py-1.5 text-slate-500">{p.client}</td>
                    <td className="py-1.5 text-right tabular-nums">{p.total.toFixed(0)}</td>
                    <td className="py-1.5">
                      <div className="h-2 bg-slate-100 rounded overflow-hidden">
                        <div className="bg-slate-500 h-full" style={{ width: `${pct}%` }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Aandachtspunten */}
      <div className="border border-slate-200 rounded p-4">
        <h3 className="font-semibold mb-3">Aandachtspunten</h3>
        <Alerts rows={rows} weeks={weeks} totalsByPersonWeek={totalsByPersonWeek} />
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, accent }) {
  const accentColor =
    accent === "red" ? "text-red-600" :
    accent === "emerald" ? "text-emerald-600" :
    accent === "amber" ? "text-amber-600" :
    "text-slate-900";
  return (
    <div className="border border-slate-200 rounded p-3 bg-white">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-2xl font-semibold ${accentColor} mt-1 tabular-nums`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function Alerts({ rows, weeks, totalsByPersonWeek }) {
  const items = [];
  const people = Object.keys(totalsByPersonWeek).filter(isRealPerson);
  people.forEach((p) => {
    weeks.forEach((w) => {
      const v = totalsByPersonWeek[p][w] || 0;
      if (v > 40) items.push({ severity: "high", text: `${p} > 40u in ${weekLabel(w)} (${formatWeekStart(w)}) — ${v}u`, key: `${p}-${w}-over` });
      else if (v < 16 && v > 0) items.push({ severity: "low", text: `${p} onderbezet in ${weekLabel(w)} (${formatWeekStart(w)}) — ${v}u`, key: `${p}-${w}-under` });
      else if (v === 0) items.push({ severity: "low", text: `${p} ongepland in ${weekLabel(w)} (${formatWeekStart(w)})`, key: `${p}-${w}-zero` });
    });
  });

  // Rijen zonder klant/project/code
  rows.forEach((r) => {
    if (!r.person) return;
    const totalRowHours = weeks.reduce((s, w) => s + (r.hours[w] || 0), 0);
    if (totalRowHours > 0 && !r.code) {
      items.push({ severity: "info", text: `${r.person} — ${r.client || "?"} / ${r.project || "?"}: tijdschrijfcode ontbreekt`, key: `code-${r.id}` });
    }
  });

  if (!items.length) return <div className="text-sm text-slate-400">Niets opvallends.</div>;

  // Sort: high → low → info
  const order = { high: 0, low: 1, info: 2 };
  items.sort((a, b) => order[a.severity] - order[b.severity]);

  return (
    <ul className="space-y-1 max-h-64 overflow-y-auto text-sm">
      {items.slice(0, 30).map((it) => (
        <li key={it.key} className="flex items-start gap-2">
          <span
            className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
              it.severity === "high" ? "bg-red-500" :
              it.severity === "low" ? "bg-amber-400" :
              "bg-slate-300"
            }`}
          />
          <span className="text-slate-700">{it.text}</span>
        </li>
      ))}
      {items.length > 30 && (
        <li className="text-xs text-slate-400 pl-4">+ {items.length - 30} meer…</li>
      )}
    </ul>
  );
}
function SummaryBlock({
  variant,
  groupMap,
  weeks,
  expanded,
  setExpanded,
  otherKey,
  otherLabel,
  weekTotal,
  grandTotal,
}) {
  const groups = Object.keys(groupMap).sort((a, b) => a.localeCompare(b));
  const toggle = (k) => setExpanded({ ...expanded, [k]: !expanded[k] });

  if (!groups.length) {
    return (
      <div className="p-4 text-center text-slate-400 border border-dashed border-slate-200 rounded">
        Nog geen data — voeg rijen toe in de planning hierboven.
      </div>
    );
  }

  const headerLabel = variant === "person" ? "Medewerker" : "Klant";

  return (
    <div className="border border-slate-200 rounded overflow-hidden">
      <div className="overflow-x-auto">
        <table className="border-collapse w-full">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left px-2 py-1.5 border-b border-slate-200 sticky left-0 bg-slate-50 min-w-[220px]" rowSpan={2}>
                {headerLabel}
              </th>
              {weeks.map((w) => (
                <th key={w} className="text-center px-1 pt-1.5 pb-0 border-slate-200 font-medium text-slate-600 min-w-[48px] leading-tight">
                  {weekLabel(w)}
                </th>
              ))}
              <th className="text-center px-2 py-1.5 border-b border-slate-200 bg-slate-200 min-w-[60px]" rowSpan={2}>Tot</th>
            </tr>
            <tr>
              {weeks.map((w) => (
                <th key={w} className="text-center px-1 pb-1.5 border-b border-slate-200 text-[10px] text-slate-400 font-normal leading-tight">
                  {formatWeekStart(w)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const isOpen = expanded[g];
              const detailRows = groupMap[g];
              return (
                <React.Fragment key={g}>
                  <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => toggle(g)}>
                    <td className="border-b border-slate-100 sticky left-0 bg-white px-2 py-1.5 font-medium">
                      <div className="flex items-center gap-1">
                        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        {g}
                      </div>
                    </td>
                    {weeks.map((w) => {
                      const v = weekTotal(g, w);
                      if (variant === "person") {
                        const { bg, text } = personCellStyle(v);
                        return (
                          <td key={w} className={`border-b border-slate-100 text-center px-1 py-1.5 ${bg} ${text}`}>
                            {v || "0"}
                          </td>
                        );
                      } else {
                        return (
                          <td key={w} className={`border-b border-slate-100 text-center px-1 py-1.5 ${clientCellShade(v)}`}>
                            {v || ""}
                          </td>
                        );
                      }
                    })}
                    <td className="border-b border-slate-100 text-center px-2 py-1.5 bg-slate-50 font-medium">
                      {grandTotal(g) || ""}
                    </td>
                  </tr>
                  {isOpen &&
                    detailRows.map((dr) => (
                      <tr key={dr.id} className="bg-slate-50/60 text-slate-600">
                        <td className="border-b border-slate-100 sticky left-0 bg-slate-50/60 px-2 py-1 pl-8 text-xs">
                          <span className="text-slate-400">{otherLabel}:</span> {dr[otherKey] || "—"}
                          {dr.project && <span className="text-slate-400"> · {dr.project}</span>}
                          {dr.code && <span className="text-slate-400"> · </span>}
                          {dr.code && <span className="text-slate-500 font-mono">{dr.code}</span>}
                        </td>
                        {weeks.map((w) => (
                          <td key={w} className="border-b border-slate-100 text-center px-1 py-1 text-xs">
                            {dr.hours[w] || ""}
                          </td>
                        ))}
                        <td className="border-b border-slate-100 text-center px-2 py-1 text-xs bg-slate-100/50">
                          {weeks.reduce((s, w) => s + (dr.hours[w] || 0), 0) || ""}
                        </td>
                      </tr>
                    ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
